import React, { useState, useEffect } from 'react';
import { Plus, Filter, Calendar, MapPin, Globe, Users, RefreshCw } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { useSystemPreset } from '../../../contexts/SystemPresetContext';
import { useAuth } from '../../../contexts/AuthContext';
import { CommunityEventsService } from '../../../services/communityEvents/CommunityEventsService';
import CommunityEventCard from './components/CommunityEventCard';
import EventDetailsPanel from './components/EventDetailsPanel';
import CreateCommunityEventModal from './modals/CreateCommunityEventModal';
import { SectionHeader } from '../../ui';
import type { CommunityEvent, CreateEventData, CreateAgendaItemData } from '../../../types/communityEvents';

interface EventsSectionProps {
  searchQuery: string;
}

const EventsSection: React.FC<EventsSectionProps> = ({ searchQuery }) => {
  const { language, isRTL } = useLocalization();
  const { presetData } = useSystemPreset();
  const { user } = useAuth();
  const [events, setEvents] = useState<CommunityEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState<string>('all');
  const [timeFilter, setTimeFilter] = useState<'all' | 'upcoming' | 'today' | 'thisWeek'>('upcoming');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  
  // View state management
  const [selectedEvent, setSelectedEvent] = useState<CommunityEvent | null>(null);

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      setLoading(true);
      const eventsData = await CommunityEventsService.getEvents();
      setEvents(eventsData);
    } catch (error) {
      console.error('Error loading events:', error);
    } finally {
      setLoading(false);
    }
  };


  const filterEventsByTime = (events: CommunityEvent[]) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekFromNow = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);

    switch (timeFilter) {
      case 'today':
        return events.filter(event => {
          const eventDate = new Date(event.startDate);
          return eventDate.toDateString() === today.toDateString();
        });
      case 'thisWeek':
        return events.filter(event => {
          const eventDate = new Date(event.startDate);
          return eventDate >= today && eventDate <= weekFromNow;
        });
      case 'upcoming':
        return events.filter(event => new Date(event.startDate) > now);
      default:
        return events;
    }
  };

  const filteredEvents = filterEventsByTime(events).filter(event => {
    const matchesSearch = searchQuery === '' || 
      event.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      event.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      event.organizerName.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesType = selectedType === 'all' || 
      event.communityEventTypeId === selectedType;
    
    return matchesSearch && matchesType;
  });

  const handleEventClick = (event: CommunityEvent) => {
    setSelectedEvent(event);
  };


  const handleEditEvent = (event: CommunityEvent) => {
    // TODO: Implement edit functionality
    console.log('Edit event:', event.id);
  };

  const handleDeleteEvent = async (eventId: string) => {
    if (window.confirm(language === 'ar' ? 'هل أنت متأكد من حذف هذه الفعالية؟' : 'Are you sure you want to delete this event?')) {
      try {
        await CommunityEventsService.deleteEvent(eventId);
        await loadEvents(); // Reload events list
        setSelectedEvent(null);
      } catch (error) {
        console.error('Error deleting event:', error);
      }
    }
  };

  // Helper function to check if current user is the event creator
  const isEventCreator = (event: CommunityEvent) => {
    return user?.id && event.organizerId === user.id;
  };

  const handleRSVP = async (eventId: string) => {
    try {
      // Implement RSVP functionality
      console.log('RSVP to event:', eventId);
    } catch (error) {
      console.error('Error RSVPing to event:', error);
    }
  };

  const handleCreateEvent = () => {
    setShowCreateModal(true);
  };

  const handleRefresh = () => {
    loadEvents();
  };

  const handleCreateEventSubmit = async (eventData: CreateEventData, agendaItems: CreateAgendaItemData[]) => {
    try {
      setCreateLoading(true);
      console.log('🚀 Starting event creation process...');
      console.log('📝 Event data:', eventData);
      console.log('📋 Agenda items:', agendaItems);
      
      const eventId = await CommunityEventsService.createEventWithAgenda(eventData, agendaItems);
      console.log('✅ Event with agenda created successfully:', eventId);
      
      // Reload events to show the new one
      console.log('🔄 Reloading events list...');
      await loadEvents();
      
      // Show success message
      setSuccessMessage(language === 'ar' ? 'تم إنشاء الفعالية بنجاح!' : 'Event created successfully!');
      
      // Close modal
      setShowCreateModal(false);
      console.log('🎉 Event creation process completed successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (error) {
      console.error('❌ Error creating event with agenda:', error);
      
      // Enhanced error handling
      let errorMessage = 'Failed to create event. Please try again.';
      
      if (error instanceof Error) {
        if (error.message.includes('Validation failed')) {
          errorMessage = error.message;
        } else if (error.message.includes('User must be authenticated')) {
          errorMessage = 'Please log in to create an event.';
        } else if (error.message.includes('network')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else {
          errorMessage = error.message;
        }
      }
      
      // You could add a toast notification here
      alert(`Error: ${errorMessage}`);
      
      throw error; // Re-throw to let the modal handle the error
    } finally {
      setCreateLoading(false);
    }
  };

  // Render the two-panel layout
  const renderListPanel = () => (
    <div className="space-y-6">
      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800 dark:text-green-200">
                {successMessage}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Filters and Actions */}
      <div className={`flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
        <div className={`flex flex-col sm:flex-row gap-4 ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
          {/* Event Type Filter */}
          <div className="relative">
            <Filter className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4`} />
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className={`${isRTL ? 'pr-10 pl-4' : 'pl-10 pr-4'} py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent min-w-[150px]`}
            >
              <option value="all">
                {language === 'ar' ? 'جميع الأنواع' : 'All Types'}
              </option>
              {presetData?.eventTypes?.map((type) => (
                <option key={type.id} value={type.id}>
                  {language === 'ar' ? type.name_ar : type.name_en}
                </option>
              ))}
            </select>
          </div>

          {/* Time Filter */}
          <div className="relative">
            <Calendar className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4`} />
            <select
              value={timeFilter}
              onChange={(e) => setTimeFilter(e.target.value as any)}
              className={`${isRTL ? 'pr-10 pl-4' : 'pl-10 pr-4'} py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent min-w-[150px]`}
            >
              <option value="all">{language === 'ar' ? 'جميع الأوقات' : 'All Time'}</option>
              <option value="upcoming">{language === 'ar' ? 'قادمة' : 'Upcoming'}</option>
              <option value="today">{language === 'ar' ? 'اليوم' : 'Today'}</option>
              <option value="thisWeek">{language === 'ar' ? 'هذا الأسبوع' : 'This Week'}</option>
            </select>
          </div>
        </div>

        {/* Create Event Button */}
        <button
          onClick={handleCreateEvent}
          className={`flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors font-medium ${isRTL ? 'flex-row-reverse' : ''}`}
        >
          <Plus className="w-4 h-4" />
          {language === 'ar' ? 'إنشاء فعالية' : 'Create Event'}
        </button>
      </div>

      {/* Events Grid */}
      {filteredEvents.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-24 h-24 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
            <Calendar className="w-12 h-12 text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-2">
            {language === 'ar' ? 'لا توجد فعاليات' : 'No Events Found'}
          </h3>
          <p className="text-slate-600 dark:text-slate-300 mb-6">
            {searchQuery ? 
              (language === 'ar' ? 'لم نجد فعاليات تطابق بحثك' : 'No events match your search') :
              (language === 'ar' ? 'ابدأ بإنشاء أول فعالية' : 'Start by creating your first event')
            }
          </p>
          {!searchQuery && (
            <button
              onClick={handleCreateEvent}
              className="bg-primary text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-dark transition-colors"
            >
              {language === 'ar' ? 'إنشاء فعالية' : 'Create Event'}
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredEvents.map((event) => (
            <CommunityEventCard
              key={event.id}
              event={event}
              onClick={() => handleEventClick(event)}
              onRSVP={() => handleRSVP(event.id)}
            />
          ))}
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[600px]">
        {/* Left Panel - List */}
        <div className="lg:col-span-1 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded w-1/3"></div>
            <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-2/3"></div>
            <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/2"></div>
          </div>
        </div>
        
        {/* Right Panel - Details */}
        <div className="lg:col-span-2 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded w-1/3"></div>
            <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-2/3"></div>
            <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <SectionHeader
        title={language === 'ar' ? 'الفعاليات' : 'Events'}
        description={language === 'ar' ? 'اكتشف وشارك في الفعاليات المجتمعية' : 'Discover and participate in community events'}
        language={language}
        isRTL={isRTL}
        refreshButton={{
          onClick: handleRefresh,
          disabled: loading
        }}
        addButton={{
          label: language === 'ar' ? 'إنشاء فعالية' : 'Create Event',
          onClick: handleCreateEvent
        }}
      >
        <div className={`flex flex-col sm:flex-row gap-3 ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
          <div className="relative">
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
              style={{ textAlign: isRTL ? 'right' : 'left' }}
            >
              <option value="all">
                {language === 'ar' ? 'جميع الأنواع' : 'All Types'}
              </option>
              {presetData?.eventTypes?.map((type) => (
                <option key={type.id} value={type.id}>
                  {language === 'ar' ? type.name_ar : type.name_en}
                </option>
              ))}
            </select>
          </div>

          <div className="relative">
            <select
              value={timeFilter}
              onChange={(e) => setTimeFilter(e.target.value as any)}
              className="px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
              style={{ textAlign: isRTL ? 'right' : 'left' }}
            >
              <option value="all">{language === 'ar' ? 'جميع الأوقات' : 'All Time'}</option>
              <option value="upcoming">{language === 'ar' ? 'قادمة' : 'Upcoming'}</option>
              <option value="today">{language === 'ar' ? 'اليوم' : 'Today'}</option>
              <option value="thisWeek">{language === 'ar' ? 'هذا الأسبوع' : 'This Week'}</option>
            </select>
          </div>
        </div>
      </SectionHeader>

      {/* Content Panels */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Panel - Events List */}
        <div className="lg:col-span-1 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
          <div className="p-6">
            {filteredEvents.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Calendar className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-2">
                  {language === 'ar' ? 'لا توجد فعاليات' : 'No Events Found'}
                </h3>
                <p className="text-slate-600 dark:text-slate-300 text-sm">
                  {searchQuery ? 
                    (language === 'ar' ? 'لم نجد فعاليات تطابق بحثك' : 'No events match your search') :
                    (language === 'ar' ? 'ابدأ بإنشاء أول فعالية' : 'Start by creating your first event')
                  }
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredEvents.map((event) => (
                  <div
                    key={event.id}
                    onClick={() => handleEventClick(event)}
                    className={`p-4 border rounded-lg cursor-pointer transition-all duration-200 hover:shadow-md ${
                      selectedEvent?.id === event.id
                        ? 'border-primary bg-primary/5 dark:bg-primary/10'
                        : 'border-slate-200 dark:border-slate-700 hover:border-primary/30'
                    }`}
                  >
                    <div className={`flex items-start gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      {event.imageURL && (
                        <img 
                          src={event.imageURL} 
                          alt={event.title}
                          className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                        />
                      )}
                      <div className={`flex-1 min-w-0 ${isRTL ? 'text-right' : 'text-left'}`}>
                        <h3 className="font-semibold text-slate-800 dark:text-white mb-1 line-clamp-1">
                          {event.title}
                        </h3>
                        <p className="text-sm text-slate-600 dark:text-slate-300 line-clamp-2 mb-2">
                          {event.description}
                        </p>
                        <div className={`flex items-center gap-4 text-xs text-slate-500 dark:text-slate-400 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(event.startDate).toLocaleDateString('en-GB', {
                              month: 'short',
                              day: 'numeric'
                            })}
                          </span>
                          <span className="flex items-center gap-1">
                            {event.isOnline ? <Globe className="w-3 h-3" /> : <MapPin className="w-3 h-3" />}
                            {event.isOnline ? 
                              (language === 'ar' ? 'أونلاين' : 'Online') : 
                              (event.city || language === 'ar' ? 'حضوري' : 'Physical')
                            }
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {event.currentAttendees}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Event Details */}
        <div className="lg:col-span-2 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
          {selectedEvent ? (
            <EventDetailsPanel
              event={selectedEvent}
              onEdit={isEventCreator(selectedEvent) ? handleEditEvent : undefined}
              onDelete={isEventCreator(selectedEvent) ? handleDeleteEvent : undefined}
            />
          ) : (
            <div className="h-full flex items-center justify-center p-6">
              <div className="text-center">
                <Calendar className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-2">
                  {language === 'ar' ? 'اختر فعالية لعرض التفاصيل' : 'Select an event to view details'}
                </h3>
                <p className="text-slate-600 dark:text-slate-300">
                  {language === 'ar' ? 'انقر على أي فعالية من القائمة لعرض تفاصيلها الكاملة' : 'Click on any event from the list to view its full details'}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Event Modal */}
      <CreateCommunityEventModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateEventSubmit}
        loading={createLoading}
      />
    </div>
  );
};

export default EventsSection;
